import { Group, Paper, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import _ from "lodash"
import { usePostHog } from "posthog-js/react"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import { SavedViewsResponses } from "@costory/types/endpoints/savedViews"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { ChartContainer } from "@costory/front/components/ChartContainer"
import { DonutChart } from "@costory/front/components/charts/DonutChart"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useChartContext } from "@costory/front/contexts/ChartContext"
import { usePageContext } from "@costory/front/contexts/PageContext"
import { apiClient } from "@costory/front/lib/apiClient"
import { queryClient } from "@costory/front/lib/queryClient"
import { ExplorerMainChart } from "@costory/front/pages/Explorer/ExplorerMainChart"
import { ExplorerTable } from "@costory/front/pages/Explorer/ExplorerTable"
import { useSavedViewQuery } from "@costory/front/queries/savedViews"
import { getBestDrillDown } from "@costory/front/utils/columns"

type Props = {
  savedView?: SavedViewsResponses.GetSavedViewDetails
}

// Keep this as a separate component to make eslint happy:
// "React Hooks must be called in a React function component or a custom React Hook function  react-hooks/rules-of-hooks"
const ChartContent = ({
  showComparisonChart,
}: {
  showComparisonChart: boolean
}) => {
  const {
    filters,
    drillDownInto,
    setExplorerChartType,
    setThreshold,
    selectedMetric,
  } = useChartContext()
  const filtersWithoutEvents = _.omit(filters, "bookmarkedEvents")
  const { explorerChartType, threshold, ...explorerFilters } =
    filtersWithoutEvents
  const posthog = usePostHog()
  const [includeForecast, setIncludeForecast] = useState(false)

  return (
    <Stack>
      <Group flex={1} align="stretch" h={400}>
        <Paper
          flex={1}
          px={32}
          py={24}
          data-intercom-target="explorer-main-chart-paper"
        >
          <Stack h={400} justify="center">
            <ExplorerMainChart
              explorerChartType={explorerChartType ?? ExplorerChartType.BAR}
              filters={explorerFilters}
              drillDownInto={drillDownInto}
              setExplorerChartType={setExplorerChartType}
              setThreshold={setThreshold}
              threshold={threshold}
              selectedMetric={selectedMetric}
              showComparisonChart={showComparisonChart}
              filterEventsByChangeDataPointPeriods={false}
              showCrossAddEvent={false}
              includeForecast={includeForecast}
              setIncludeForecast={setIncludeForecast}
            />
          </Stack>
        </Paper>
        <Paper
          w={350}
          px={32}
          py={24}
          data-intercom-target="explorer-pie-chart-paper"
        >
          <Stack h="none" align="center" justify="center">
            <DonutChart
              filters={explorerFilters}
              height={300}
              displayTrend={true}
              setExplorerChartType={setExplorerChartType}
              drillDownInto={(value) => {
                posthog.capture("Explorer - Donut Chart Drill Down", {
                  groupBy: filters.groupBy,
                  dimensionValue: value,
                  source: "DonutChart",
                  context: "Explorer",
                })
                drillDownInto(
                  explorerFilters.groupBy,
                  value,
                  getBestDrillDown(explorerFilters.groupBy),
                )
              }}
            />
          </Stack>
        </Paper>
      </Group>
      <ExplorerTable filters={explorerFilters} drillDownInto={drillDownInto} />
    </Stack>
  )
}

const Explorer = ({ savedView }: Props) => {
  const { setSecondaryTitle } = usePageContext()

  useEffect(() => {
    setSecondaryTitle(savedView ? savedView.name : undefined)
  }, [savedView, setSecondaryTitle])
  const [showComparisonChart, { toggle: toggleShowComparisonChart }] =
    useDisclosure(false)
  return (
    <ChartContainer
      savedView={savedView}
      redirectPage="explorer"
      key={savedView ? savedView.id : "no-view"}
      showComparisonChart={showComparisonChart}
      toggleShowComparisonChart={toggleShowComparisonChart}
    >
      <ChartContent showComparisonChart={showComparisonChart} />
    </ChartContainer>
  )
}

export const ExplorerPage = () => {
  const { viewId } = useParams()
  const savedViewQuery = useSavedViewQuery(viewId)

  // Track first explorer visit for onboarding
  useEffect(() => {
    const trackFirstExplorerVisit = async () => {
      try {
        await apiClient.post("/explore/track-explorer-visit")
        // Immediately refresh onboarding status so the step is checked off without a full refresh
        await queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] })
      } catch (error) {
        // Silently fail - this is not critical
        console.log("Failed to track explorer visit:", error)
      }
    }

    trackFirstExplorerVisit()
  }, [])

  if (viewId) {
    return (
      <QueryWrapper query={savedViewQuery}>
        {({ data: savedView }) => <Explorer savedView={savedView} />}
      </QueryWrapper>
    )
  }

  return <Explorer />
}
